/**
 * Start and stop a timeline on OPBridge, from anywhere (2026-10-05).
 *
 * Moved out of the v2 editor (TimelineV2.tsx `launchOnBridge` / `stop`) so
 * Navigate's Timelines pane (app-study.html: "start or stop a timeline without
 * opening the editor") runs the SAME launch, not a second one that could
 * drift: push when OPBridge does not already hold this exact timeline, then
 * start (a song) or fire (an action) on its home machine, and say what
 * happened.
 */

import type { Timeline } from '@opbuttons/schema';
import type { FireResult } from '../opbridge/dispatch.ts';
import {
  startTimelineAction, fireTimelineAction, stopTimelineAction,
  summarizeTimelineDispatch, timelineHomeTarget, timelinePartsDropWarning, timelineOfflineNote, newerCopyOnBridge, newerCopyMessage,
  splitPushFailures, missedPartsNote,
} from './bridgeSync.ts';
import { applyPartsOutcome, timelinePartsCurrent, pushTimelineParts, forgetTimelinePush, noteLaunchLoop } from './autoSync.ts';
import { pushToast } from '../ui/toast.ts';
import { noteTransportLaunch } from './transport.ts';
import { plainFireReason } from '../opbridge/dispatch.ts';

/** Push every part of `t` that is not already on its computer (2026-10-28, "the whole rig follows"); one `ok` naming
 *  the press target when all were current, so a toast reads the same either way. */
async function pushParts(t: Timeline, target: string): Promise<FireResult[]> {
  const { results } = await pushTimelineParts(t);
  if (results.length > 0) applyPartsOutcome(t, results);
  return results.length > 0 ? results : [{ target, kind: 'ok' }];
}

/**
 * Start (a song) or fire (an action) `t`: its parts pushed first, then ONE press on one computer holding a part, which
 * OPBridge carries to the rest (S340 `timeline_rig_follows`).
 *
 * A song's Play is PLAY NOW (operator, 2026-10-25/27/28): with Live stopped it runs at once at Live's last tempo and
 * moves onto Live's bar lines when Live plays; with Live gone the timeline's own tempo leads (OPBridge S342); with Live
 * playing it lands on the next bar line. `launch` is the other start: it waits for Live's first bar line once Live plays.
 * `editor` marks the timeline editor's Play (OPBridge S351): with the clock's leader not heard, that press takes the
 * clock on this PC so a rehearsal plays. Never set it from a surface a gig uses (Navigate, pads, cues).
 */
export function launchTimelineOnBridge(t: Timeline, opts: { fromBar?: number; launch?: boolean; editor?: boolean } = {}): void {
  const name = t.name;
  const home = timelineHomeTarget(t);
  // Every surface showing the transport sees the press at once (2026-10-16: Navigate's Play left it to the next
  // reading, so a start waiting for its bar line could pass unseen). The editor and the pads note it themselves too.
  if (t.tier === 'song') noteTransportLaunch(home, t.id, opts.fromBar ?? 0);
  // This Play is the timeline's own Loop: a Launcher's override ends here (C-3).
  noteLaunchLoop(t.id, null);
  const skipped = timelinePartsDropWarning(t);
  const pushStep: Promise<FireResult[]> = timelinePartsCurrent(t)
    ? Promise.resolve([skipped
      ? ({ target: home, kind: 'ok', partialDropWarning: skipped } as FireResult)
      : ({ target: home, kind: 'ok' } as FireResult)])
    : pushUnlessNewer(t, home, () => pushParts(t, home));
  const offline = timelineOfflineNote(t);
  void pushStep.then((allPushResults) => {
    // Only the press target's own part stops the start (audit C-2); the rest plays and the toast names what does not.
    const { blocking, missed } = splitPushFailures(allPushResults, [home]);
    if (blocking.length > 0) {
      const s = summarizeTimelineDispatch(name, allPushResults, null);
      pushToast(s.kind, s.message);
      return;
    }
    const pushResults = allPushResults.filter((r) => r.kind === 'ok');
    const notes = [offline, missedPartsNote(missed)].filter(Boolean).join(' ') || null;
    const fire = withRepush(
      t.id,
      () => (t.tier === 'song' ? startTimelineAction(t.id, home, opts.launch ? 'nextBar' : 'playNow', opts.fromBar, opts.editor ? 'editor' : undefined) : fireTimelineAction(t.id, home)),
      () => pushParts(t, home),
    );
    void fire.then((fireResults) => {
      const s = summarizeTimelineDispatch(name, pushResults, fireResults);
      // A computer holding a part is off or missed it: the rest plays, and the toast says what does not (operator, 2026-10-28).
      const said = notes && s.kind !== 'error' ? { kind: 'warn' as const, message: `${s.message} ${notes}` } : s;
      // Its song (OPBridge S318): say where it plays, or why it did not come (the timeline plays either way).
      toastWithSong(said, fireResults);
    });
  }).catch((err: unknown) => {
    pushToast('error', `"${name}" failed to launch: ${err instanceof Error ? err.message : String(err)}`);
  });
}

/** A launch's push, unless OPBridge holds a NEWER copy of the timeline: then nothing is pushed, that copy plays, and
 *  the toast says so (`newerCopyOnBridge`). Every launch path goes through this. */
export async function pushUnlessNewer(t: Timeline, target: string, push: () => Promise<FireResult[]>): Promise<FireResult[]> {
  const newer = await newerCopyOnBridge(t, target);
  if (!newer) {
    const results = await push();
    // OPBridge refused it itself (S324, 409 `newer_copy`): same outcome, said the same way.
    const refused = results.find((r) => r.newerCopy !== undefined);
    if (refused) pushToast('warn', newerCopyMessage(t, refused.newerCopy!), 8000);
    return results;
  }
  pushToast('warn', newerCopyMessage(t, newer.modified), 8000);
  return [{ target, kind: 'ok' }];
}

/** OPBridge's answer to a start or fire of a timeline it does not hold (opbridge-timelines `start_at_bar`, a 422). */
const NOT_HELD = /no timeline with that id/i;

/** A start or fire; when OPBridge answers that it does not hold the timeline (its copy deleted from another device, its
 *  data reset), the timeline is pushed again and the start tried once more (2026-10-16). Before, a launch skipped the
 *  push whenever this tab remembered making it, so every press after the loss failed the same way, on stage. Every
 *  launch path goes through this: the editor and Navigate (below), the canvas Launcher and the Legacy editor. */
export async function withRepush(
  timelineId: string,
  go: () => Promise<FireResult[]>,
  repush: () => Promise<FireResult[]>,
): Promise<FireResult[]> {
  const first = await go();
  if (!first.some((r) => r.kind !== 'ok' && NOT_HELD.test(r.detail ?? ''))) return first;
  forgetTimelinePush(timelineId);
  const pushed = await repush();
  if (pushed.length === 0 || pushed.some((r) => r.kind !== 'ok')) return first;
  return go();
}

/** The launch's toast, saying whether its song came with it (OPBridge S318) and why not when it did not. Shared by every
 *  launch path (2026-10-16: the Timeline Launcher said "launched" when the song did not come). */
export function toastWithSong(given: { kind: 'ok' | 'warn' | 'error'; message: string }, fireResults: FireResult[]): void {
  // Live is not open, so the timeline sets the tempo (OPBridge S342): said, so a show that is not in time with Live is
  // never a surprise (operator, 2026-10-28: "opbridge ... is controlled by the timeline").
  const leads = fireResults.find((r) => r.leads)?.leads;
  const s = leads && given.kind !== 'error'
    ? { ...given, message: `${given.message} Live isn't open, so this timeline sets the tempo: ${Math.round(leads.bpm * 100) / 100} BPM.` }
    : given;
  const song = fireResults.find((r) => r.song)?.song;
  if (song && s.kind !== 'error') {
    if (song.playing) pushToast(s.kind, `${s.message} With its song${song.output ? ` on ${song.output}` : ''}.`);
    else pushToast('warn', `${s.message} Without its song: ${song.message ?? song.reason ?? 'OPBridge did not say why'}.`);
    return;
  }
  pushToast(s.kind, s.message);
}

/** Stop a playing Song on its home machine; says so when it cannot. */
export function stopTimelineOnBridge(t: Timeline): void {
  if (t.tier !== 'song') return;
  void stopTimelineAction(timelineHomeTarget(t)).then((r) => {
    const failed = r.filter((x) => x.kind !== 'ok');
    if (failed.length > 0) pushToast('error', `"${t.name}" did not stop: ${plainFireReason(failed[0]!)}.`);
  });
}
